import { observable, action, computed, runInAction } from 'mobx';
import util from 'preact-util';
import PubSub, { topics } from '../lib/pubsub';

class PowerofficeStore {
    @observable customers = [];
    @observable products = [];
    @observable invoices = [];
    @observable loading = false;
    @observable error = null;
    @observable onboardingStatus = null;
    @observable onboardingError = null;
    @observable voucherPreview = null;
    @observable voucherPreviewLoading = false;
    @observable voucherHistory = [];
    @observable voucherHistoryLoading = false;
    @observable voucherPostLoading = false;
    @observable voucherPostResult = null;

    constructor() {
        this.name = 'poweroffice';
    }

    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @action
    async saveCredentials({ clientKey, environment = 'production' }) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/poweroffice/save-credentials', {
                method: 'POST',
            }, {
                clientKey,
                environment,
            });

            if (response.status === 200) {
                console.log('PowerOffice credentials saved successfully');
                // Reload user info to get updated customer data BEFORE updating UI state
                const userStore = require('./user').default;
                await userStore.getInfo();
            }

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to save credentials';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async revokeCredentials() {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/poweroffice/revoke', {
                method: 'POST',
            });

            if (response.status === 200) {
                console.log('PowerOffice credentials revoked successfully');
                // Reload user info to get updated customer data BEFORE updating UI state
                const userStore = require('./user').default;
                await userStore.getInfo();
            }

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to revoke credentials';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getCustomers(params = {}) {
        this.loading = true;
        this.error = null;

        try {
            const queryString = new URLSearchParams(params).toString();
            const url = `/api/integrations/poweroffice/customers${queryString ? '?' + queryString : ''}`;

            const response = await util.fetchApi(url);

            runInAction(() => {
                if (response.status === 200) {
                    this.customers = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch customers';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async createCustomer(customerData) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/poweroffice/customers', {
                method: 'POST',
            }, customerData);

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to create customer';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getProducts(params = {}) {
        this.loading = true;
        this.error = null;

        try {
            const queryString = new URLSearchParams(params).toString();
            const url = `/api/integrations/poweroffice/products${queryString ? '?' + queryString : ''}`;

            const response = await util.fetchApi(url);

            runInAction(() => {
                if (response.status === 200) {
                    this.products = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch products';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async createProduct(productData) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/poweroffice/products', {
                method: 'POST',
            }, productData);

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to create product';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async getInvoices(params = {}) {
        this.loading = true;
        this.error = null;

        try {
            const queryString = new URLSearchParams(params).toString();
            const url = `/api/integrations/poweroffice/invoices${queryString ? '?' + queryString : ''}`;

            const response = await util.fetchApi(url);

            runInAction(() => {
                if (response.status === 200) {
                    this.invoices = response.data || [];
                } else {
                    this.error = response.message || 'Failed to fetch invoices';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    @action
    async createInvoice(invoiceData) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/poweroffice/invoices', {
                method: 'POST',
            }, invoiceData);

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to create invoice';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    /**
     * Create invoice from a sale
     * Automatically creates/links customer and products in PowerOffice
     * @param {number} saleId - The sale ID to create invoice from
     */
    @action
    async createInvoiceFromSale(saleId) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/poweroffice/invoices/${saleId}`, {
                method: 'POST',
            }, {});

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to create invoice from sale';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    /**
     * Upload the sale's invoice PDF as an attachment on the existing PowerOffice sales order.
     * Requires the sale to have already been transferred (has a salesOrderId).
     * @param {number} saleId - The sale ID whose PDF will be uploaded
     * @param {object} [options] - Optional body text to include in the PDF
     */
    @action
    async uploadInvoiceAttachment(saleId, { body } = {}) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/poweroffice/invoices/${saleId}/attachment`, {
                method: 'POST',
            }, body ? { body } : {});

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to upload invoice attachment';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    /**
     * Simulate invoice creation from a sale (preview without calling PowerOffice API)
     * @param {number} saleId - The sale ID to simulate
     */
    @action
    async simulateInvoice(saleId) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi(`/api/integrations/poweroffice/invoices/${saleId}?simulate=true`, {
                method: 'POST',
            }, {});

            runInAction(() => {
                if (response.status !== 200) {
                    this.error = response.message || 'Failed to simulate invoice';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    /**
     * Update specific sales from PowerOffice
     * Fetches latest status for specified sale IDs
     * @param {number[]} saleIds - Array of sale IDs to update
     */
    @action
    async updateInvoices(saleIds) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/poweroffice/invoices/update', {
                method: 'PATCH',
            }, {
                saleIds,
            });

            runInAction(() => {
                if (response.status !== 202 && response.status !== 200) {
                    this.error = response.message || 'Failed to update invoices from PowerOffice';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    /**
     * Sync invoices from PowerOffice by date range
     * Updates payment status and creates SalePayment records for new payments
     * @param {string} invoiceDateFrom - Start date (ISO format)
     * @param {string} invoiceDateTo - End date (ISO format)
     */
    @action
    async updateInvoicesByDate(invoiceDateFrom, invoiceDateTo) {
        this.loading = true;
        this.error = null;

        try {
            const response = await util.fetchApi('/api/integrations/poweroffice/invoices/update-by-date', {
                method: 'PATCH',
            }, {
                invoiceDateFrom,
                invoiceDateTo,
            });

            runInAction(() => {
                if (response.status !== 202 && response.status !== 200) {
                    this.error = response.message || 'Failed to sync invoices from PowerOffice';
                }
                this.loading = false;
            });

            return response;
        } catch (error) {
            runInAction(() => {
                this.error = error.message;
                this.loading = false;
            });
            throw error;
        }
    }

    /**
     * Initiate onboarding flow
     * Redirects user to PowerOffice authorization page
     */
    initiateOnboarding(customerId, environment = 'demo') {
        const token = util.getJwtToken();
        const apiServer = util.getApiServer();

        let url = `${apiServer}/api/integrations/poweroffice/onboarding-initiate?customer=${customerId}&environment=${environment}`;
        if (token) {
            url += `&jwtToken=${token}`;
        }

        window.location.href = url;
    }

    /**
     * Check for onboarding callback parameters in URL after redirect
     * Call this when page loads with success/error params
     */
    @action
    async checkOnboardingCallback() {
        const urlParams = new URLSearchParams(window.location.search);
        const hash = window.location.hash;

        // Check both URL and hash for parameters
        const hashParams = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');

        const success = urlParams.get('success') || hashParams.get('success');
        const error = urlParams.get('error') || hashParams.get('error');
        const message = urlParams.get('message') || hashParams.get('message');
        const provider = urlParams.get('provider') || hashParams.get('provider');

        // Only handle PowerOffice onboarding callbacks
        if (provider !== 'poweroffice') {
            return;
        }

        if (success === 'true') {
            console.log('PowerOffice onboarding callback: success');
            // Reload user info to get updated customer data with credentials
            const userStore = require('./user').default;
            await userStore.getInfo();

            runInAction(() => {
                this.onboardingStatus = 'connected';
                this.onboardingError = null;
                PubSub.publish(topics.NOTIFY, {
                    type: 'success',
                    message: 'PowerOffice connected successfully! You can now use PowerOffice integration.',
                });
            });
            this.cleanupOnboardingUrl();
        } else if (error) {
            runInAction(() => {
                this.onboardingStatus = 'error';
                this.onboardingError = decodeURIComponent(message || error);
                PubSub.publish(topics.NOTIFY, {
                    type: 'error',
                    message: `Failed to connect PowerOffice: ${decodeURIComponent(message || error)}`,
                });
            });
            this.cleanupOnboardingUrl();
        }
    }

    /**
     * Clean up onboarding parameters from URL
     */
    cleanupOnboardingUrl() {
        const url = new URL(window.location.href);
        url.searchParams.delete('success');
        url.searchParams.delete('error');
        url.searchParams.delete('message');
        url.searchParams.delete('provider');

        // Also clean hash params
        let hash = url.hash;
        if (hash.includes('?')) {
            const [hashPath, hashQuery] = hash.split('?');
            const hashParams = new URLSearchParams(hashQuery);
            hashParams.delete('success');
            hashParams.delete('error');
            hashParams.delete('message');
            hashParams.delete('provider');
            const cleanHashQuery = hashParams.toString();
            url.hash = cleanHashQuery ? `${hashPath}?${cleanHashQuery}` : hashPath;
        }

        window.history.replaceState({}, '', url.toString());
    }

    async getVoucherPreview({ startDate, endDate, excludeInvoice }) {
        this.updateKeyValue('voucherPreviewLoading', true);
        this.updateKeyValue('voucherPreview', null);
        const params = { startDate, endDate };
        if (excludeInvoice) {
            params.excludeInvoice = '1';
        }
        const response = await util.fetchApi('/api/integrations/poweroffice/voucher-preview', {
            method: 'GET',
        }, params);
        this.updateKeyValue('voucherPreviewLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('voucherPreview', response.data);
                return response;
            default:
                console.error('powerofficeStore.getVoucherPreview', response);
                return response;
        }
    }

    async postVoucher({ startDate, endDate, periodType, excludeInvoice }) {
        this.updateKeyValue('voucherPostLoading', true);
        this.updateKeyValue('voucherPostResult', null);
        const response = await util.fetchApi('/api/integrations/poweroffice/vouchers', {
            method: 'POST',
        }, {
            startDate,
            endDate,
            periodType,
            excludeInvoice,
        });
        this.updateKeyValue('voucherPostLoading', false);
        switch (response.status) {
            case 201:
                this.updateKeyValue('voucherPostResult', response);
                this.getVoucherPreview({ startDate, endDate, excludeInvoice });
                this.getVoucherHistory();
                return response;
            default:
                console.error('powerofficeStore.postVoucher', response);
                this.updateKeyValue('voucherPostResult', response);
                return response;
        }
    }

    async getVoucherHistory() {
        this.updateKeyValue('voucherHistoryLoading', true);
        const response = await util.fetchApi('/api/integrations/poweroffice/vouchers');
        this.updateKeyValue('voucherHistoryLoading', false);
        switch (response.status) {
            case 200:
                this.updateKeyValue('voucherHistory', response.data || []);
                return response;
            default:
                console.error('powerofficeStore.getVoucherHistory', response);
                return response;
        }
    }

    @action clearVoucherData() {
        this.voucherPreview = null;
        this.voucherPostResult = null;
    }

    @computed
    get hasCredentials() {
        const userStore = require('./user').default;
        return !!(userStore.customer?.powerOfficeCredentials?.clientKey);
    }

    @computed
    get environment() {
        const userStore = require('./user').default;
        return userStore.customer?.powerOfficeCredentials?.environment || 'production';
    }
}

const store = new PowerofficeStore();
export default store;
